import DeviceType from '@/helpers/DeviceType';
import { DEVICE_SETTINGS, SETTING_LABELS } from '@/helpers/DeviceSettings/catalog';
import OsdLanguage, { OSD_LOCALE } from '@/helpers/OsdLanguage';
import StorageInspector from '@/helpers/StorageHandler/Inspector';
import { findAppMatch } from '@/helpers/Cast/appMatch';

// Aufbereitung der Diagnose-Daten fuer die Info-Seite UND fuer die
// get_info-Antwort an den Socket-Admin.
//
// Warum hier und nicht in Info.vue: der Admin soll genau das sehen, was auf dem
// Geraet steht - Zeile fuer Zeile, mit denselben Beschriftungen. Zwei
// Implementierungen derselben Tabellen wuerden auseinanderlaufen, sobald eine
// Zeile dazukommt, und die Ferndiagnose zeigte dann etwas anderes als der
// Bildschirm vor Ort. Deshalb sind die Zeilenbauer reine Funktionen, die beide
// Seiten aufrufen.
export default class Diagnostics {
  // Anzeigewert: null/undefined/'' -> Gedankenstrich, damit "nicht vorhanden"
  // sichtbar von "false"/"0" unterscheidbar bleibt.
  static value(v) {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'ja' : 'nein';
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  // Production / Staging anhand der konfigurierten API-URL bzw. des Build-Modes.
  static environment() {
    const api = process.env.API_PORTAL || process.env.PORTAL || '';
    const mode = process.env.MODE_ENV || process.env.RUN_ENV || process.env.NODE_ENV || '';
    const isStaging = /staging|localhost|\.media\b/i.test(api) || /dev/i.test(mode);
    return isStaging ? 'Staging' : 'Production';
  }

  // Lesbares Datum (DD.MM.YYYY HH:mm:ss) aus Unix-Timestamp oder ISO-Datum -
  // sonst null. Nur fuer Schluessel, bei denen ein Datum plausibel ist: eine
  // beliebige 10-stellige Zahl ist kein Zeitstempel.
  static formatDate(key, value) {
    let date;
    const isUnixKey = /updated|created|date|time|install|expire|registered|seen/i.test(key);
    if ((typeof value === 'number' || /^\d{10}$|^\d{13}$/.test(value)) && isUnixKey) {
      const num = Number(value);
      date = new Date(num < 1e12 ? num * 1000 : num); // Sekunden vs. Millisekunden
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value)) {
      date = new Date(value);
    } else {
      return null;
    }
    if (isNaN(date.getTime())) return null;
    const p = (n) => (n < 10 ? '0' + n : '' + n);
    return (
      p(date.getDate()) + '.' + p(date.getMonth() + 1) + '.' + date.getFullYear() +
      ' ' + p(date.getHours()) + ':' + p(date.getMinutes()) + ':' + p(date.getSeconds())
    );
  }

  // Quelle, aus der die angezeigte webOS-Version letztlich stammt.
  static webosSource(diag) {
    const info = diag && diag.webosInfo;
    return (info && info.source) || 'unbekannt';
  }

  // Alle Rohwerte der Versionserkennung. Nur auf LG sinnvoll.
  static webosRows(diag) {
    if (!DeviceType.isLG) return [];
    const d = diag || {};
    const info = d.webosInfo || {};
    const val = Diagnostics.value;
    return [
      { key: 'WebOS-Generation (verwendet)', value: val(d.webos) },
      { key: 'Numerisch (Feature-Gate)', value: val(d.webosMajor) },
      { key: 'platform_version (Gerät)', value: val(info.platformVersion) },
      {
        key: '→ als Generation verwertbar',
        // Hospitality-Firmware liefert hier die Firmware-Version (z.B. 3.62.00),
        // nicht die webOS-Generation. Solche Werte werden verworfen statt zu
        // einer erfundenen Generation umgebogen.
        value: info.platformVersion
          ? info.fromPlatformVersion || 'NEIN — sieht wie eine Firmware-Version aus'
          : '—',
      },
      { key: 'hcap_middleware_version (Gerät)', value: val(info.hcapMiddlewareVersion) },
      { key: 'hcap.js (App, static/hcap.js)', value: val(info.hcapJsVersion) },
      { key: 'model_name', value: val(info.modelName) },
      { key: 'webOS.deviceInfo sdkVersion', value: val(info.sdkVersion) },
      { key: 'Modell (UA)', value: val(info.uaModel) },
      {
        key: '→ Generation aus Modellcode',
        // Nur vermessene Serien, keine Heuristik über die Buchstabenfolge: die
        // gilt für Consumer-TVs, nicht für Hospitality (UK660H = 2025).
        value: info.fromModel
          ? info.fromModel + ' (vermessen)'
          : 'Serie nicht vermessen — in DeviceType._webOSFromModel ergänzen',
      },
      { key: 'Firmware (UA)', value: val(info.uaFirmware) },
      { key: 'Pro:Centric-Browser (PCBR)', value: val(info.pcbrVersion) },
      { key: 'Chrome-Version (UA)', value: val(info.chromeVersion) },
      { key: 'UA explizit', value: val(info.fromUaExplicit) },
      { key: 'UA über Chrome abgeleitet', value: val(info.fromUaChrome) },
      {
        key: 'Chrome-Ableitung verwertbar',
        // Bei PCBR ist die Chrome-Version die des Pro:Centric-Browsers und sagt
        // nichts über die webOS-Generation.
        value: info.uaChromeUnreliable ? 'NEIN (PCBR)' : 'ja',
      },
      {
        key: 'Verwendeter Wert belastbar',
        value: info.unreliable ? 'NEIN — nur PCBR-Chrome-Ableitung' : 'ja',
      },
    ];
  }

  // Aktuelle OSD-Sprache des TV plus die Locale, die zur App-Sprache gehoert.
  // Weichen die ab, erscheinen Cast/AirPlay in der falschen Sprache.
  static osdLanguage(langKey) {
    const cur = OsdLanguage.get();
    const want = OSD_LOCALE[langKey] || '-';
    if (!cur) return 'nicht lesbar (Soll: ' + want + ')';
    const name = cur.osdName ? ' (' + cur.osdName + ')' : '';
    // Kam der Wert nur ueber den LOCALE-Ersatz, sagen wir es dazu - er laeuft
    // einem frischen Setzen hinterher (siehe helpers/OsdLanguage).
    const via = cur.via === 'locale' ? ' [ueber Systemsprache]' : '';
    return cur.value + name + via + (cur.value === want ? '' : '  -  Soll: ' + want);
  }

  // Ergebnis des SSID-Rueckvergleichs. Bewusst dreiwertig: "nicht geprueft"
  // (SoftAP noch nicht konfiguriert) unterscheidet sich von "nicht pruefbar"
  // (Geraet gibt tv_name nicht heraus) und von einer echten Abweichung.
  static softApVerifyText(v) {
    if (!v) return null;
    if (!v.readable) return 'nicht prüfbar (' + v.detail + ')';
    if (v.matched) return 'ja: ' + v.ssid;
    // Praefix der Firmware ist kein Fehler: unser Name steht vollstaendig drin,
    // der TV haengt nur etwas davor (BU800: '[TV]'). Anzeige und QR benutzen den
    // gesendeten Namen, der Gast findet das Netz also.
    if (v.prefixed) return 'ja mit Firmware-Präfix: gesendet "' + v.ssid + '" (gesetzt "' + v.expected + '")';
    return 'NEIN — gesetzt "' + v.expected + '", aktiv "' + v.ssid + '"';
  }

  // Wie softApVerifyText, aber fuer das Passwort. Getrennt, weil SSID und
  // Passwort unterschiedlich gut lesbar sind: die SSID gibt jede Firmware
  // heraus, das Passwort erst ab IDPN 304 (LG) bzw. je nach Getter (Samsung).
  static softApKeyVerifyText(v) {
    if (!v) return null;
    if (!v.keyReadable) return 'nicht prüfbar (Gerät gibt das Passwort nicht heraus)';
    if (v.keyMatched) return 'ja';
    return 'NEIN — gesetzt "' + v.expectedKey + '", aktiv "' + v.key + '"';
  }

  // Apps, die IDCAP als "token: authNeeded" meldet - die brauchen noch eine
  // Freischaltung ueber LG (Vertrag/Token), sind also nicht per API loesbar.
  static idcapAuthNeeded(diag) {
    const caps = (diag && diag.capabilities) || null;
    const apps = (caps && caps.idcapApps) || [];
    const needed = apps.filter((a) => a.token === 'authNeeded');
    if (!apps.length) return null;
    return needed.length ? needed.map((a) => a.name || a.id).join(', ') : 'keine';
  }

  // Cast-Strategie und was das Geraet tatsaechlich gemeldet hat.
  static castRows({ diag, softApVerify, langKey }) {
    const d = diag;
    if (!d) return [];
    const val = Diagnostics.value;
    const caps = d.capabilities || {};
    const net = caps.network || {};
    const p = d.pairing || {};
    const rows = [
      { key: 'Plattform', value: val(d.platform) },
      { key: 'Pairing-Strategie', value: val(d.strategy) },
      { key: 'Cast unterstützt', value: val(d.supported) },
      // Auf LG entscheidet der Checkout-Snapshot darüber, ob das Löschen der
      // Gastdaten überhaupt wirkt: requestCheckout SETZT auf ihn ZURÜCK. Steht
      // hier "nein", quittiert das Gerät den Löschbefehl zwar, entfernt aber
      // nichts (siehe helpers/Cast/LG.js).
      // null heißt "nicht geprüft" (Samsung): ob deleteAppPrivateInfo die
      // OTT-Anmeldungen entfernt, hängt an der Firmware - am BU800 (Tizen 6.5)
      // nachweislich nicht, am U800F (Tizen 9) ja.
      {
        key: 'Löschen nachweisbar wirksam',
        value: d.clearVerified === null ? 'nicht geprüft (je nach Firmware)' : val(d.clearVerified),
      },
      // Der Grund steht auch dann dabei, wenn der Snapshot VORHANDEN ist: wurde
      // er erzwungen, während noch ein Gast am Gerät hing, friert er womöglich
      // dessen Anmeldung ein - und stellt sie bei jedem Checkout wieder her.
      // Das muss ablesbar bleiben, nicht nur in der Logzeile von damals.
      {
        key: 'Checkout-Snapshot',
        value: d.snapshotTaken
          ? d.snapshotReason
            ? 'vorhanden (' + d.snapshotReason + ')'
            : 'vorhanden'
          : d.snapshotReason
            ? 'fehlt (' + d.snapshotReason + ')'
            : val(false),
      },
      { key: 'Natives Cast-Pairing (≥ 25)', value: val(d.nativeCast) },
      { key: 'Erkennung durchgelaufen', value: val(!!caps.detected) },
      // SoftAP-Verbindungsmeldung. "Listener aktiv" = der TV meldet
      // Verbindungen überhaupt; der Rohwert zeigt, was zuletzt ankam.
      // Laeuft der Hotspot ueberhaupt? Live vom Geraet (isSoftAPEnabled).
      // Steht hier "nein", nuetzen SSID, Passwort und QR nichts - dann muss
      // erst der Hotspot zurueck.
      {
        key: 'SoftAP laeuft',
        value: val(d.softApEnabled === null || typeof d.softApEnabled === 'undefined' ? 'keine Auskunft' : d.softApEnabled),
      },
      { key: 'SoftAP-Listener aktiv', value: val(!!p.listening) },
      {
        key: 'SoftAP verbunden',
        value: val(
          p.watching
            ? p.connected
            : p.listening
            ? 'noch kein Ereignis vom Gerät'
            : 'meldet dieses Gerät nicht'
        ),
      },
      { key: 'SoftAP-Geräte', value: val(p.clients || 0) },
      { key: 'Letzte SoftAP-Meldung', value: val(p.raw || 'noch keine') },
      // Cast-, AirPlay- und Spiegelungs-Oberflächen richten sich nach der
      // OSD-Sprache des TV, nicht nach unserer App-Sprache.
      { key: 'TV-OSD-Sprache', value: val(Diagnostics.osdLanguage(langKey)) },
    ];
    if (d.platform !== 'lg') {
      return rows.concat([
        // Samsung dokumentiert die TEP-Netzwerk-APIs nicht öffentlich und die
        // Namen wechseln je Firmware. Diese Liste zeigt, was das Gerät wirklich
        // mitbringt - ohne sie ist nicht zu unterscheiden, ob ein
        // Schreibzugriff scheiterte oder die Funktion gar nicht existiert.
        { key: 'SoftAP-API des Geräts', value: val(caps.softApApi) },
        // Vollstaendige Liste von webapis.network. Auf den SoftAP-Namen war
        // keine Auskunft ueber verbundene Geraete zu finden - falls es eine
        // gibt, steht sie hier (z.B. eine DHCP-Abfrage).
        { key: 'Netzwerk-API des Geräts (alle)', value: val(caps.networkApi) },
        // Die Cloning-Funktionen des Geräts.
        //
        // Die App wendet bisher nur an (restoreCloneData auf Samsung,
        // requestCloning auf LG). Ob eine Firmware auch die Gegenrichtung
        // kennt - ein Clonefile schreiben oder auslesen, gesichert wie
        // ungesichert -, steht in keinem Quellcode: auf Samsung kommt
        // webapis.clone vom Gerät. Diese Zeile beantwortet die Frage dort, wo
        // sie zu beantworten ist - am Gerät.
        { key: 'Clone-API des Geräts', value: val(caps.cloneApi) },
        // Ohne eindeutiges Schaltfunktions-Paar wird der SoftAP NICHT neu
        // gestartet: ein halber Neustart ließe den Hotspot aus, und der wäre nur
        // noch über das Service-Menü des TV zurückzuholen.
        { key: 'SoftAP schaltbar (aus/ein)', value: val(caps.softApSwitch) },
        // Entscheidend fuer den Passwortwechsel: kann der TV seinen Hotspot
        // wieder einschalten? Wenn nicht, wird er nie geschaltet und ein neues
        // Passwort gilt erst nach einem Neustart des TV.
        {
          key: 'SoftAP-Neustart moeglich',
          value: val(
            typeof caps.softApRestartable === 'undefined'
              ? 'unbekannt'
              : caps.softApRestartable
              ? true
              : 'nein - Passwortwechsel braucht Neustart'
          ),
        },
        // Gibt es die Abfrage, hängt die Verbindungsanzeige nicht mehr allein an
        // den Ereignissen des TV.
        // Die eigentliche Quelle fuer "Handy verbunden". Steht hier "ja" und
        // "Letzte SoftAP-Meldung" trotzdem auf "noch keine", feuert der
        // Listener nicht - dann liegt es nicht an der App.
        { key: 'SoftAP-Client-Listener', value: val(!!caps.softApClientInfoListener) },
        // Google Cast, wie es Samsungs Hotel-SI-Checkliste prueft: der Dienst
        // soll nach dem Hochlaufen READY sein, und das Usage-Bit ("Help us
        // improve", eureka_info opt_in.stats) nach dem Checkout aus - die
        // Firmware schaltet es erst beim Oeffnen der Cast-Einstellungen ein.
        { key: 'Google-Cast-Status', value: val(caps.gcStatus || (caps.hasGoogleCast ? 'unbekannt' : 'kein Google Cast')) },
        { key: 'Usage-Bit (Help us improve)', value: val(caps.gcUsageReporting || 'nicht lesbar') },
        // Betrieblich wichtig: ohne Sofort-Loeschen verschwinden die
        // Anmeldedaten des Gastes erst mit einem echten Neustart - und im
        // Virtual Standby gibt es keinen.
        {
          key: 'Sofort-Loeschen (Anmeldedaten)',
          value: val(
            typeof caps.clearCredentialsNow === 'undefined'
              ? 'unbekannt'
              : caps.clearCredentialsNow
              ? true
              : 'nein - nur beim Neustart'
          ),
        },
        { key: 'SoftAP-Client-Abfrage', value: val(caps.softApClientQuery) },
        { key: 'SoftAP-SSID bestätigt', value: val(Diagnostics.softApVerifyText(softApVerify)) },
        { key: 'SoftAP-Passwort bestätigt', value: val(Diagnostics.softApKeyVerifyText(softApVerify)) },
      ]);
    }
    return rows.concat([
      { key: 'Cast-Einstieg (App-ID)', value: val(caps.castEntryId) },
      { key: 'Gefundene Kandidaten', value: val(caps.castEntryCandidates) },
      { key: 'Google Cast vorhanden', value: val(caps.googleCast) },
      { key: 'AirPlay vorhanden', value: val(caps.airplay) },
      { key: 'Screen Share (App-IDs)', value: val(caps.screenShareIds) },
      // Bestimmt, welche CP-Apps LG dem Gerät anbietet. Eine falsch gesetzte
      // Region erklärt einen unvollständigen App-Katalog.
      { key: 'Service-Country', value: val(caps.serviceCountry) },
      {
        key: 'Apps installiert / im Katalog',
        // getApplicationList() liefert einen Katalog; nur valid=true ist
        // wirklich installiert und startbar.
        value:
          (caps.apps ? caps.apps.length : '?') + ' / ' + (caps.catalog ? caps.catalog.length : '?'),
      },
      {
        key: 'Preloaded-Apps gemeldet',
        value: val(caps.preloadedApps ? caps.preloadedApps.length : null),
      },
      { key: 'WLAN-SSID (verbunden)', value: val(net.ssid) },
      { key: 'WLAN-Passwort auslesbar', value: val(!!net.key) },
      { key: 'Netzwerk-QR möglich', value: val(!!caps.networkQr) },
      // SSID = Property tv_name, Passwort = soft_ap_password.
      { key: 'SoftAP-QR möglich', value: val(!!caps.softApQr) },
      { key: 'SoftAP-SSID (tv_name)', value: val(caps.properties && caps.properties.tv_name) },
      // Auf manchen Firmwares (43UK660H) ist tv_name schreib-, aber nicht
      // lesbar. Dann steht oben nichts, und trotzdem ist der SoftAP-QR baubar -
      // diese Zeile ist der Beweis.
      {
        key: 'SoftAP-SSID schreibbar',
        value: caps.tvNameWritable === null ? 'nicht versucht' : val(!!caps.tvNameWritable),
      },
      // Rückvergleich nach dem Schreiben: deckt auf, wenn die Firmware tv_name
      // still ablehnt und der QR eine falsche SSID zeigen würde.
      { key: 'SoftAP-SSID bestätigt', value: val(Diagnostics.softApVerifyText(softApVerify)) },
      // Dito für das Passwort: soft_ap_password ist erst ab IDPN 304 lesbar.
      // Steht hier eine Abweichung, sendet der SoftAP mit anderen Zugangsdaten
      // als der QR zeigt - dann kommt das Handy nicht ins Netz.
      { key: 'SoftAP-Passwort bestätigt', value: val(Diagnostics.softApKeyVerifyText(softApVerify)) },
      { key: 'IP', value: val(net.ip) },
      // IDCAP laeuft parallel zu HCAP mit (LG stellt HCAP ein). Hier steht, ob
      // der Daemon antwortet - Grundlage für die spätere Migration.
      { key: 'IDCAP verfügbar', value: val(!!(caps.idcap && caps.idcap.working)) },
      {
        key: 'IDCAP Version / Mode',
        value: val(
          caps.idcap
            ? (caps.idcap.apiVersion || '?') +
              ' / ' +
              (caps.idcap.mode === null ? '?' : caps.idcap.mode)
            : null
        ),
      },
      { key: 'IDCAP Status', value: val(caps.idcap && caps.idcap.detail) },
      // token=authNeeded zeigt pro App, ob noch eine Lizenz-/Token-Freischaltung
      // über LG fehlt - das sagt HCAP mit valid=false nicht.
      { key: 'IDCAP Apps (Token-Freischaltung nötig)', value: val(Diagnostics.idcapAuthNeeded(d)) },
    ]);
  }

  // Jede lesende SoftAP-Funktion des Geraets mit ihrer Antwort. Aendert sich
  // eine Zeile, sobald ein Smartphone am SoftAP haengt, ist das die gesuchte
  // Client-Abfrage - Samsung dokumentiert sie nicht.
  static softApProbeRows(diag) {
    const probe = (diag && diag.softApProbe) || [];
    return probe.map((r) => ({ key: r.name, value: Diagnostics.value(r.text) }));
  }

  // Welche cast-relevanten Properties die Firmware wirklich kennt.
  static castPropertyRows(diag) {
    const caps = (diag && diag.capabilities) || null;
    if (!caps || !caps.properties) return [];
    return Object.keys(caps.properties).map((key) => ({
      key,
      value: Diagnostics.value(caps.properties[key]),
    }));
  }

  // Vorinstallierte Apps: Name -> numerische Id.
  static preloadedAppRows(diag) {
    const caps = (diag && diag.capabilities) || null;
    if (!caps || !caps.preloadedApps) return [];
    return caps.preloadedApps.map((a, i) => ({
      key: a.name || a.id || '#' + i,
      value: Diagnostics.value(a.id),
    }));
  }

  // Fuer Google Cast relevante Streaming-Apps, gefiltert aus den installierten
  // Apps. Abgleich ueber App-Name oder App-ID mit derselben Rangfolge wie die
  // Install-Seite (helpers/Cast/appMatch.js) - sonst stand hier fuer YouTube
  // "YoutubeKids", nur weil es im Verzeichnis zuerst kam.
  static castApps(installedApps) {
    const list = installedApps || [];
    const matchers = [
      { label: 'Netflix', keys: ['netflix'] },
      { label: 'YouTube', keys: ['youtube'] },
      { label: 'Amazon Prime Video', keys: ['amazon', 'prime video', 'primevideo'] },
      { label: 'Paramount+', keys: ['paramount'] },
      { label: 'Disney+', keys: ['disney'] },
      { label: 'Apple TV', keys: ['apple tv', 'appletv'] },
      { label: 'Max / HBO', keys: ['hbo', 'hbomax', 'wbd', 'max'] },
      { label: 'Spotify', keys: ['spotify'] },
      { label: 'DAZN', keys: ['dazn'] },
      { label: 'Twitch', keys: ['twitch'] },
      { label: 'WOW / Sky', keys: ['wow', 'sky'] },
      { label: 'Rakuten TV', keys: ['rakuten'] },
      { label: 'RTL+', keys: ['rtl plus', 'rtlplus', 'rtl+'] },
      { label: 'Joyn', keys: ['joyn'] },
      { label: 'Magenta TV', keys: ['magenta'] },
      { label: 'Plex', keys: ['plex'] },
      { label: 'ARD Mediathek', keys: ['ard mediathek', 'ardmediathek'] },
      { label: 'ZDFmediathek', keys: ['zdf'] },
      { label: 'arte', keys: ['arte'] },
      { label: 'waipu.tv', keys: ['waipu'] },
      { label: 'Zattoo', keys: ['zattoo'] },
      { label: 'Crunchyroll', keys: ['crunchyroll'] },
      { label: 'Deezer', keys: ['deezer'] },
      { label: 'Amazon Music', keys: ['amazon music', 'amazonmusic'] },
      { label: 'Apple Music', keys: ['apple music', 'applemusic'] },
      { label: 'TuneIn', keys: ['tunein'] },
    ];
    const result = [];
    for (const m of matchers) {
      const app = findAppMatch(list, { aliases: m.keys });
      if (app) {
        result.push({
          label: m.label,
          id: app.id,
          name: app.name,
          version: app.version,
          // Auf Hospitality ist die App-Liste ein Katalog: valid=false heisst
          // "im Katalog, aber nicht installiert" - die App ist nicht startbar.
          installed: app.valid !== false,
        });
      }
    }
    return result;
  }

  // Alle Zustands-Abschnitte der Info-Seite als Daten: [{ title, rows }].
  //
  // Das ist die Form, in der der Socket-Admin sie eins zu eins anzeigen kann -
  // gleiche Beschriftungen, gleiche Reihenfolge, gleiche Werte wie am TV. Leere
  // Abschnitte fallen weg, genau wie auf der Seite (v-if="rows.length").
  //
  // Die App-LISTEN gehoeren ausdruecklich NICHT hierher: der Katalog eines
  // Hospitality-TV hat leicht dreistellig viele Eintraege und wuerde die
  // Zustandsdaten, um die es hier geht, in der Antwort untergehen lassen. Die
  // gibt es auf eigene Anfrage - siehe appSections.
  // Die geraeteinternen Einstellungen (Service-/Pro:Centric-Menue) als Zeilen.
  //
  // Was das Geraet nicht kennt, steht ausdruecklich als "nicht unterstützt"
  // drin statt zu fehlen. Die Menue-Ids und Property-Namen im Katalog sind zum
  // Teil geraten (siehe helpers/DeviceSettings/catalog.js) - diese Tabelle ist
  // der Beleg, welcher Name auf welchem Modell antwortet, und der Zusatz in
  // Klammern sagt, ueber welchen Weg der Wert kam.
  //
  // Der Schnappschuss wird nicht hier geholt: das Lesen ist asynchron, diese
  // Klasse liefert Zeilen. Die Info-Seite und die get_info-Antwort reichen ihn
  // herein - so wie die Cast-Diagnose auch.
  static settingsRows(settings) {
    if (!settings) return [];
    const rows = [];
    if (settings.error) rows.push({ key: 'Fehler', value: settings.error });
    const values = settings.values || {};
    const details = settings.details || {};
    DEVICE_SETTINGS.forEach((entry) => {
      const label = SETTING_LABELS[entry.key] || entry.key;
      const has = Object.prototype.hasOwnProperty.call(values, entry.key);
      if (!has) {
        rows.push({ key: label, value: 'nicht unterstützt' });
        return;
      }
      const via = details[entry.key] ? ' (' + details[entry.key] + ')' : '';
      rows.push({ key: label, value: Diagnostics.value(values[entry.key]) + via });
    });
    return rows;
  }

  // Die Einstellungs-APIs, die das Geraet tatsaechlich mitbringt: ein Eintrag
  // je Namensraum mit seinen Methodennamen.
  //
  // Das ist das Gegenstueck zur Tabelle darueber. Dort steht, welche Kandidaten
  // aus dem Katalog geantwortet haben; hier steht, was das Geraet ueberhaupt
  // anbietet - inklusive der Namen, auf die niemand gekommen waere. Genau so
  // ist auf einem BU800 die Clone-API vermessen worden.
  static settingsApiRows(settings) {
    const api = (settings && settings.api) || null;
    if (!api) return [];
    return Object.keys(api).map((ns) => ({
      key: ns + ' (' + (api[ns] || []).length + ')',
      value: (api[ns] || []).join(', ') || '—',
    }));
  }

  // Speicher und Dateien am Geraet - die Antwort auf "kommen wir an ein
  // Clonefile heran?".
  //
  // Erzeugen laesst sich eins per API nicht (weder Samsung noch LG haben eine
  // Export-Funktion, siehe helpers/StorageProbe). Die Frage ist, ob eine ueber
  // das Hotelmenue auf USB geschriebene Datei anschliessend LESBAR ist - dann
  // muesste niemand den Stick zum Rechner tragen. Diese Tabelle sagt, was das
  // Geraet an Speichern meldet und was darauf liegt.
  static storageProbeRows(probe) {
    if (!probe) return [];
    const rows = [];
    if (probe.error) rows.push({ key: 'Fehler', value: probe.error });
    Object.keys(probe.api || {}).forEach((ns) => {
      rows.push({ key: ns + ' (' + (probe.api[ns] || []).length + ')', value: (probe.api[ns] || []).join(', ') || '—' });
    });
    if (probe.fileListError) {
      // Die Absage nennt den Parameter, den die Firmware erwartet - genau so
      // kam bei getInstallerMenuItem heraus, dass er `key` heisst.
      rows.push({ key: 'Dateiliste', value: probe.fileListError });
    }
    if (probe.storagesRaw) rows.push({ key: 'Antwort (roh)', value: probe.storagesRaw });
    if (probe.readTest) {
      // Der Punkt, auf den es beim Clonefile ankommt: kommen wir an den INHALT
      // einer Datei? Auflisten allein reicht dafuer nicht. Gemessen werden zwei
      // Wege - der native (openStream) und der ueber die Adresse (toURI).
      const t = probe.readTest;
      (t.results || []).forEach((r) => {
        rows.push({
          key: 'Lesetest ' + (r.via || '?') + ' (' + (t.storage || '?') + ': ' + (t.file || '?') + ')',
          value: r.ok
            ? r.bytes + ' Bytes gelesen (' + (r.sample || '') + ')'
            : 'fehlgeschlagen - ' + (r.detail || ''),
        });
      });
    }
    (probe.storages || []).forEach((st, i) => {
      const label = st.label || st.name || ('Speicher ' + (i + 1));
      const info = [st.type, st.state].filter(Boolean).join(' / ');
      rows.push({ key: 'Speicher: ' + label, value: info || Diagnostics.value(st) });
    });
    (probe.files || []).forEach((scan) => {
      const entries = scan.entries || [];
      if (!scan.readable) {
        rows.push({ key: 'Inhalt ' + scan.label, value: 'nicht lesbar' });
        return;
      }
      if (entries.length === 0) {
        rows.push({ key: 'Inhalt ' + scan.label, value: 'leer' });
        return;
      }
      entries.forEach((e) => {
        const size = e.size !== undefined ? ' (' + e.size + ' Bytes)' : e.dir ? ' (Ordner)' : '';
        rows.push({ key: scan.label + ': ' + (e.name || '?'), value: (e.path || '') + size });
      });
    });
    return rows;
  }

  static sections({ diag, softApVerify, langKey, settings, storage }) {
    const probe = Diagnostics.softApProbeRows(diag);
    const props = Diagnostics.castPropertyRows(diag);
    const known = props.filter((r) => r.value !== '—').length;
    const out = [
      { title: 'WebOS-Version (Quelle: ' + Diagnostics.webosSource(diag) + ')', rows: Diagnostics.webosRows(diag) },
      { title: 'Cast-Diagnose', rows: Diagnostics.castRows({ diag, softApVerify, langKey }) },
      { title: 'SoftAP-Funktionen am Gerät (' + probe.length + ')', rows: probe },
      {
        title: 'Cast-Properties am Gerät (' + known + ' von ' + props.length + ' bekannt)',
        rows: props,
      },
    ];
    const deviceSettings = Diagnostics.settingsRows(settings);
    if (deviceSettings.length) {
      const supported = (settings && settings.supported && settings.supported.length) || 0;
      out.push({
        title:
          'Geräteinterne Einstellungen (' + supported + ' von ' + DEVICE_SETTINGS.length + ' unterstützt)',
        rows: deviceSettings,
      });
    }
    const storageRows = Diagnostics.storageProbeRows(storage);
    if (storageRows.length) {
      out.push({ title: 'Speicher und Dateien am Gerät', rows: storageRows });
    }
    const settingsApi = Diagnostics.settingsApiRows(settings);
    if (settingsApi.length) {
      out.push({ title: 'Einstellungs-API des Geräts', rows: settingsApi });
    }
    return out.concat(Diagnostics.storageSections()).filter((s) => s.rows.length);
  }

  // Was in den vier Speichern des Geraets steht.
  //
  // Hier und nicht in einer eigenen Abfrage, damit Info-Seite und
  // get_info-Antwort dieselben Zeilen zeigen - dafuer ist diese Klasse da. Die
  // Werte sind gekuerzt und je Speicher gedeckelt (siehe Inspector), die
  // Abschnitte bleiben also auch auf einem vollgelaufenen localStorage lesbar.
  //
  // Der interne Speicher ist Samsung-only. Auf LG steht dort bewusst der Grund
  // statt einer leeren Tabelle - "kein Eintrag" und "gibt es nicht" sind zwei
  // verschiedene Aussagen, und nur die zweite erklaert, warum ein LG nach
  // einem WGT-Update seine Registrierung verlieren kann.
  static storageSections() {
    let data;
    try {
      data = StorageInspector.read();
    } catch (e) {
      return [{ title: 'Speicher', rows: [{ key: 'Fehler', value: String((e && e.message) || e) }] }];
    }
    const label = {
      cookie: 'Cookie',
      local: 'LocalStorage',
      session: 'SessionStorage',
      internal: 'Interner Speicher (keymanager)',
    };
    return ['internal', 'local', 'cookie', 'session'].map((name) => {
      const store = data[name] || { entries: [] };
      const rows = (store.entries || []).map((e) => ({
        key: e.key,
        value: e.value === '' ? '(leer)' : e.value,
      }));
      if (store.available === false) {
        rows.push({ key: 'Verfügbar', value: 'nein - ' + (store.reason || 'unbekannt') });
      }
      if (store.error) rows.push({ key: 'Fehler', value: store.error });
      if (store.truncated) {
        rows.push({ key: '…', value: store.truncated + ' weitere Einträge nicht gezeigt' });
      }
      if (!rows.length) rows.push({ key: 'Einträge', value: 'keine' });
      const count = store.total || 0;
      return { title: 'Speicher: ' + label[name] + ' (' + count + ')', rows: rows };
    });
  }

  // Die App-Abschnitte der Info-Seite - separat abrufbar, weil sie die
  // Zustandsdaten sonst zuschuetten.
  //
  // Reihenfolge nach abnehmendem Nutzen: die cast-relevanten Apps sind das,
  // wonach man sucht; die Preloaded-Liste liefert die NUMERISCHEN Ids, die auf
  // LG zum Starten von Premium-OTT gebraucht werden; der volle Katalog ist der
  // Nachschlagepunkt, wenn eine App unter einem unerwarteten Namen laeuft.
  static appSections({ diag, installedApps }) {
    const list = installedApps || [];
    const apps = Diagnostics.castApps(list);
    const preloaded = Diagnostics.preloadedAppRows(diag);
    const out = [
      {
        title: 'Cast-relevante Apps (' + apps.length + ' von ' + list.length + ' im Katalog)',
        rows: apps.map((a) => ({
          key: a.label,
          value:
            a.id +
            (a.version ? ' — v' + a.version : '') +
            (a.installed ? '' : ' — nicht installiert (valid=false)'),
        })),
      },
      { title: 'Preloaded-Apps (' + preloaded.length + ')', rows: preloaded },
      {
        title: 'Alle Apps am Gerät (' + list.length + ')',
        rows: list.map((a, i) => ({
          key: a.name || a.id || '#' + i,
          // valid=false heisst auf Hospitality "im Katalog, aber nicht
          // installiert" - die App ist dann nicht startbar.
          value:
            (a.id || '—') +
            (a.version ? ' — v' + a.version : '') +
            (a.valid === false ? ' — nicht installiert (valid=false)' : ''),
        })),
      },
    ];
    return out.filter((s) => s.rows.length);
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Diagnostics.js